import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { listRoleStatuses, listStackSetsAccounts, retryRole } from '../../../services/stacksetsApi';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { PortalLoading } from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, listCardSx, sectionHeadingSx, emptyTextSx, splitRowSx, actionRowSx,
} from '../portalStyles';

const phaseColor = (p) => ({
  provisioned: 'success',
  failed: 'error',
  pending: 'default',
  provisioning: 'info',
  removing: 'warning',
  removed: 'default',
}[p] || 'default');

const TRANSIENT_PHASES = ['pending', 'provisioning', 'removing'];

// RoleStatusPanel shows per-account execution-role provisioning (one row per
// onboarded account) with a Retry action on failed rows — the EnrollmentPage
// per-account grid, adapted to StackSets (IAM is global, so no region column).
// showRetry=false hides the Retry action (used on the in_progress transition
// screen, where the backend rejects retries until the fan-out finishes).
export default function RoleStatusPanel({ adminAccountId, onQuiesce, showRetry = true }) {
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({});
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(() => new Set());
  const pollRef = useRef(null);
  // onQuiesce is typically an inline closure from the parent; keep the latest
  // in a ref so the long-lived poll tick never calls a stale one.
  const onQuiesceRef = useRef(onQuiesce);
  useEffect(() => { onQuiesceRef.current = onQuiesce; });

  const refresh = useCallback(async () => {
    try {
      const res = await listRoleStatuses();
      setRows(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Account names are best-effort decoration — never blank the grid when the
  // accounts endpoint fails (EnrollmentPage precedent).
  useEffect(() => {
    (async () => {
      try {
        const res = await listStackSetsAccounts();
        // Backend returns a bare slice of {accountId, name, status}; read
        // defensively so a wrapped/snake_case shape still decorates rows.
        const list = res?.data?.accounts || res?.data || [];
        const map = {};
        list.forEach((a) => {
          const id = a.accountId || a.account_id;
          if (id) map[id] = a.name || a.accountName || '';
        });
        setNames(map);
      } catch (_) {
        setNames({});
      }
    })();
  }, []);

  // Poll while any role is transitioning (StackSetsList pattern): keyed on a
  // derived boolean primitive, so a retry that flips a row back to "pending"
  // after mount re-arms the poll. The interval stops itself inside the tick
  // only when a SUCCESSFUL response confirms quiescence — a failed fetch keeps
  // polling. Deliberately no cleanup in this effect: setRows on each tick
  // would otherwise destroy-and-recreate the interval; a separate
  // unmount-only effect handles cleanup.
  const hasTransient = !!rows?.some((r) => TRANSIENT_PHASES.includes(r.phase));
  useEffect(() => {
    if (!hasTransient || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const res = await listRoleStatuses().catch(() => null);
      if (!res) return; // transient error — keep polling
      const next = res?.data || [];
      setRows(next);
      const anyTransient = next.some((r) => TRANSIENT_PHASES.includes(r.phase));
      if (!anyTransient && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // Provisioning settled — let the parent refresh config so a
        // partial_failure → enabled rollup is reflected without a reload.
        onQuiesceRef.current?.();
      }
    }, POLL_INTERVAL_MS);
  }, [hasTransient]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const onRetry = async (accountId) => {
    setRetrying((cur) => new Set(cur).add(accountId));
    try {
      await retryRole(accountId);
      // Row flips to "pending" server-side; refetch so hasTransient re-arms
      // the poll immediately.
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRetrying((cur) => {
        const next = new Set(cur);
        next.delete(accountId);
        return next;
      });
    }
  };

  const acctLabel = (id) => (names[id] ? `${names[id]} (${id})` : id);
  const provisioned = (rows || []).filter((r) => r.phase === 'provisioned').length;
  const failed = (rows || []).filter((r) => r.phase === 'failed').length;

  return (
    <Paper elevation={0} sx={listCardSx}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ ...splitRowSx, mb: 1 }}>
          <Typography sx={sectionHeadingSx}>Execution roles</Typography>
          {rows && (
            <Box sx={{ ...actionRowSx, gap: 1 }}>
              <Chip size="small" label={`${provisioned}/${rows.length} provisioned`} sx={statusChipSx(intentFromColor(provisioned === rows.length ? 'success' : 'default'))} />
              {failed > 0 && <Chip size="small" label={`${failed} failed`} sx={statusChipSx('error')} />}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
          Prism provisions CKPrism-StackSetExecutionRole in every onboarded account.
          {showRetry ? ' Retry any account whose provisioning failed.' : ''}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{error}</Alert>}
      </Box>
      {!rows ? (
        <PortalLoading label="Loading execution roles…" py={5} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: 'right' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, index) => (
                <TableRow key={r.accountId} hover sx={rowSx(index)}>
                  <TableCell sx={bodyCellSx}>
                    {acctLabel(r.accountId)}
                    {r.accountId === adminAccountId && <Chip label="admin" size="small" sx={{ ...statusChipSx('purple'), ml: 1 }} />}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, maxWidth: 420 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={formatStatusLabel(r.phase)} size="small" sx={statusChipSx(intentFromColor(phaseColor(r.phase)))} />
                      {TRANSIENT_PHASES.includes(r.phase) && <CircularProgress size={14} thickness={5} sx={{ color: CK.blue }} />}
                    </Stack>
                    {r.phase === 'failed' && r.errorMessage && (
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.red, display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
                        {r.errorMessage}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    {showRetry && r.phase === 'failed' && (
                      <Button
                        size="small"
                        color="warning"
                        disabled={retrying.has(r.accountId)}
                        onClick={() => onRetry(r.accountId)}
                      >
                        Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <Typography sx={emptyTextSx}>
                      No accounts yet — onboard an account to have Prism provision its execution role.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
